import { defineModule, definePermission } from '@shared/contract'

// Preise & Aktionen: Preisgruppen, Preislisten mit Staffeln und Gültigkeit, Massenänderung mit Vorschau,
// Preisauskunft, Aktionen und Abschriften mit Vorschlag und Freigabe, Regaletiketten.
//
// Rechte (db/seeds/demo/roles.ts, Abschnitt „Preise und Aktionen“):
//   * Preislisten gelten für den ganzen Betrieb – lesen, pflegen und freigeben sind mandantenweite Rechte.
//   * Eine Filiale SCHLÄGT Aktionen für ihre Standorte vor (`promotion.propose`); wirksam werden sie erst mit der
//     Freigabe durch eine zweite Person (`promotion.approve`).
//   * Eine Abschrift (MHD-Abverkauf an einem Standort) darf direkt anlegen, wer `markdown.create` hält – bis zur
//     Grenze `maxDiscountBp` seiner Rolle. Darüber bleibt nur der Vorschlag.
//   * Einkaufspreise und Spannen sieht nur, wer `costs.read` hält.
const GROUP = 'Preise und Aktionen'

export default defineModule({
  id: 'pricing',
  title: 'Preise & Aktionen',
  description: 'Preisgruppen, Preislisten, Massenänderung, Preisauskunft, Aktionen mit Freigabe, Regaletiketten.',
  dependsOn: ['platform', 'masterdata'],
  permissions: [
    definePermission({
      key: 'pricing.price.read',
      label: 'Preislisten und Verkaufspreise lesen',
      group: GROUP,
      description: 'Preisgruppen, Preislisten, Preisverlauf und Preisauskunft. Einkaufspreise und Spannen nur mit „Einstandspreise sehen“.',
      locationScoped: false,
    }),
    definePermission({
      key: 'pricing.price.write',
      label: 'Preislisten und Preise pflegen',
      group: GROUP,
      description: 'Preisgruppen und Preislisten anlegen, Preise mit Stichtag setzen, Massenänderungen vorbereiten, Preislisten importieren.',
      locationScoped: false,
    }),
    definePermission({
      key: 'pricing.price.approve',
      label: 'Massenänderungen von Preisen freigeben',
      group: GROUP,
      description: 'Eine vorbereitete Massenänderung wird erst mit der Freigabe einer zweiten Person wirksam.',
      locationScoped: false,
    }),
    definePermission({
      key: 'pricing.promotion.read',
      label: 'Aktionen lesen',
      group: GROUP,
      locationScoped: false,
    }),
    definePermission({
      key: 'pricing.promotion.write',
      label: 'Aktionen für alle Standorte anlegen und beenden',
      group: GROUP,
      description: 'Wirksam wird eine Aktion erst mit der Freigabe – außer die Person hält selbst das Freigaberecht.',
      locationScoped: false,
    }),
    definePermission({
      key: 'pricing.promotion.propose',
      label: 'Aktionen für den eigenen Standort vorschlagen',
      group: GROUP,
      description: 'Der Vorschlag ändert keinen Preis; er wartet auf die Freigabe.',
    }),
    definePermission({
      key: 'pricing.promotion.approve',
      label: 'Aktionen freigeben oder ablehnen',
      group: GROUP,
      description: 'Nie den eigenen Vorschlag (Vier-Augen-Prinzip).',
      locationScoped: false,
    }),
    definePermission({
      key: 'pricing.markdown.create',
      label: 'Abschriften am eigenen Standort direkt anlegen',
      group: GROUP,
      description: 'Preisnachlass für einen Abverkauf (Restposten, Auslaufware) ohne Freigabe – bis zur Grenze der Rolle.',
      limits: [{ key: 'maxDiscountBp', label: 'Höchster Nachlass einer Abschrift', unit: 'bp' }],
    }),
    definePermission({
      key: 'pricing.label.print',
      label: 'Regaletiketten drucken',
      group: GROUP,
      description: 'Etiketten vormerken, drucken und als gedruckt abhaken – an den eigenen Standorten.',
    }),
    // Werbe-Mitteilungen (docs/integration/bestell-app.md §17): Merxo legt sie an, der angebundene Vertriebskanal
    // holt sie ab, versendet sie an Personen mit Einwilligung und meldet das Ergebnis.
    definePermission({
      key: 'pricing.campaign.read',
      label: 'Werbe-Mitteilungen lesen',
      group: GROUP,
      description: 'Angekündigte Aktionen im Vertriebskanal mit Zustand und gemeldetem Ergebnis.',
      locationScoped: false,
    }),
    definePermission({
      key: 'pricing.campaign.write',
      label: 'Aktionen im Vertriebskanal ankündigen',
      group: GROUP,
      description: 'Werbe-Mitteilung zu einer Aktion anlegen und abbrechen, solange der Kanal sie nicht versendet. Gesendet wird nur an Personen mit Einwilligung.',
      locationScoped: false,
    }),
    definePermission({
      key: 'pricing.campaign.deliver',
      label: 'Werbe-Mitteilungen als Vertriebskanal abholen',
      group: GROUP,
      description: 'Nur für den technischen Zugang des Vertriebskanals: bereite Mitteilungen übernehmen und das Ergebnis des Versands melden.',
      locationScoped: false,
    }),
  ],
})
